import React, { useEffect, useMemo, useState } from "react";

export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Shared list-table settings: persisted page size + column visibility.
 *
 * Pass `storageKey` as a stable identifier (e.g. "user-list", "order-list").
 * `allColumns` is an array of:
 *   { key, title, defaultVisible, render(record), className? }
 *
 * Returns rendered column descriptors (`dynamicColumns`) ready to merge with
 * an action column and pass to an Ant Table.
 */
export function useTableSettings({
    storageKey,
    allColumns,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    defaultPageSize = 10,
}) {
    const COLUMN_PREFS_KEY = `${storageKey}:visible-columns:v1`;
    const PAGE_SIZE_KEY = `${storageKey}:page-size:v1`;

    const defaultVisibleKeys = useMemo(
        () => allColumns.filter((c) => c.defaultVisible).map((c) => c.key),
        [allColumns],
    );

    const loadVisibleKeys = () => {
        try {
            const raw = localStorage.getItem(COLUMN_PREFS_KEY);
            if (!raw) return defaultVisibleKeys;
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) return defaultVisibleKeys;
            const known = new Set(allColumns.map((c) => c.key));
            const filtered = parsed.filter((k) => known.has(k));
            return filtered.length ? filtered : defaultVisibleKeys;
        } catch {
            return defaultVisibleKeys;
        }
    };

    const loadPageSize = () => {
        try {
            const raw = localStorage.getItem(PAGE_SIZE_KEY);
            const n = parseInt(raw, 10);
            if (pageSizeOptions.includes(n)) return n;
        } catch { }
        return defaultPageSize;
    };

    const [pageSize, setPageSize] = useState(loadPageSize);
    const [visibleColumnKeys, setVisibleColumnKeys] =
        useState(loadVisibleKeys);

    useEffect(() => {
        try {
            localStorage.setItem(
                COLUMN_PREFS_KEY,
                JSON.stringify(visibleColumnKeys),
            );
        } catch { }
    }, [visibleColumnKeys, COLUMN_PREFS_KEY]);

    useEffect(() => {
        try {
            localStorage.setItem(PAGE_SIZE_KEY, String(pageSize));
        } catch { }
    }, [pageSize, PAGE_SIZE_KEY]);

    const toggleColumn = (key) => {
        setVisibleColumnKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
        );
    };

    const resetColumns = () => setVisibleColumnKeys(defaultVisibleKeys);

    const dynamicColumns = useMemo(
        () =>
            allColumns
                .filter((c) => visibleColumnKeys.includes(c.key))
                .map((c) => ({
                    key: c.key,
                    title: c.title,
                    className: c.className,
                    render: (_, record) => <div>{c.render(record)}</div>,
                })),
        [allColumns, visibleColumnKeys],
    );

    return {
        pageSize,
        setPageSize,
        pageSizeOptions,
        visibleColumnKeys,
        setVisibleColumnKeys,
        toggleColumn,
        resetColumns,
        dynamicColumns,
        allColumns,
    };
}
